import React, { useCallback, useEffect, useMemo } from 'react';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { useSendMessageToPreview } from './useSendMessageToPreview';
import { PostMessagableWindow, SMLAppMessages } from './types';

/**
 * Tells the preview which block is open (`SMLApp.selectBlock`) and which one is under the pointer in
 * the panel (`SMLApp.hoverBlock`). It sends again on every change and when the frame loads.
 */
export function useSendBlockStateToPreview(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  smartLink: SerializedSmartLink | undefined,
  selectedWidgetUUID: string | null | undefined,
  hoveredWidgetUUID: string | null | undefined
) {
  const editUrl = smartLink?.recentSmartLinkVersion.editUrl;
  const domain = useMemo(() => (editUrl ? new URL(editUrl).origin : ''), [editUrl]);
  const send = useSendMessageToPreview(domain);

  const sendSelected = useCallback(() => {
    const target = iframeRef.current?.contentWindow as PostMessagableWindow | null | undefined;
    if (target === null || target === undefined || selectedWidgetUUID === undefined) {
      return;
    }
    send(target, { type: SMLAppMessages.selectBlock, data: { widgetUUID: selectedWidgetUUID } });
  }, [iframeRef, selectedWidgetUUID, send]);

  const sendHovered = useCallback(() => {
    const target = iframeRef.current?.contentWindow as PostMessagableWindow | null | undefined;
    if (target === null || target === undefined || hoveredWidgetUUID === undefined) {
      return;
    }
    send(target, { type: SMLAppMessages.hoverBlock, data: { widgetUUID: hoveredWidgetUUID } });
  }, [hoveredWidgetUUID, iframeRef, send]);

  useEffect(sendSelected, [sendSelected]);
  useEffect(sendHovered, [sendHovered]);

  return useCallback(() => {
    sendSelected();
    sendHovered();
  }, [sendHovered, sendSelected]);
}
